import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, ChevronLeft, ChevronRight, User, TrendingUp, 
  DollarSign, Calendar, Target, Shield, Brain, Users, 
  BarChart3, Activity, Award, Star, Zap,
  Clock, CheckCircle, AlertTriangle, Info
} from 'lucide-react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from 'recharts';

interface PsychometricProfile {
  id: string;
  name: string;
  description: string;
  color: string;
  characteristics: string[];
  communicationStyle: string;
  decisionMaking: string;
  riskTolerance: string;
  leadershipStyle: string;
  values: string[];
  motivators: string[];
  challenges: string[];
  recommendations: string[];
}

interface ClientData {
  id: string;
  name: string;
  industry: string;
  size: string;
  relationship: string;
  satisfaction: number;
  psychometricProfile: PsychometricProfile;
  financial: {
    ca: number;
    profit: number;
    budget_ti: number;
    roi: number;
    growth: number;
  };
  projects: Array<{
    name: string;
    status: string;
    progress: number;
    budget: number;
    timeline: string;
    priority: 'High' | 'Medium' | 'Low';
  }>;
  roadmap: {
    q1: string;
    q2: string;
    q3: string;
    q4: string;
  };
  committee: {
    next: string;
    frequency: string;
    participants: string[];
  };
  kpis: {
    availability: number;
    responseTime: number;
    satisfaction: number;
    innovation: number;
  };
  security: {
    nistScore: number;
    compliance: number;
    incidents: number;
    lastAudit: string;
  };
}

interface Slide {
  id: string;
  title: string;
  subtitle?: string;
  content: React.ReactNode;
  icon: any;
  color: string;
}

interface DashboardClientCompletProps {
  isOpen: boolean;
  onClose: () => void;
}

const psychometricProfiles: PsychometricProfile[] = [
  {
    id: 'visionnaire',
    name: 'Visionnaire Stratégique',
    description: 'Leader orienté innovation et transformation digitale',
    color: '#8E44AD',
    characteristics: ['Innovation', 'Vision long terme', 'Transformation', 'Excellence'],
    communicationStyle: 'Direct, inspirant, orienté résultats',
    decisionMaking: 'Rapide, basé sur la vision et l\'impact',
    riskTolerance: 'Élevée - prêt à innover',
    leadershipStyle: 'Inspirant, déléguant, orienté performance',
    values: ['Innovation', 'Excellence', 'Croissance', 'Impact'],
    motivators: ['Reconnaissance', 'Défis', 'Autonomie', 'Impact business'],
    challenges: ['Patience avec les processus', 'Détails opérationnels'],
    recommendations: ['Focus sur ROI innovation', 'Démonstrations concrètes', 'Communication directe']
  },
  {
    id: 'analytique',
    name: 'Analytique Méthodique',
    description: 'Gestionnaire rigoureux et orienté données',
    color: '#3498DB',
    characteristics: ['Rigueur', 'Analyse', 'Processus', 'Qualité'],
    communicationStyle: 'Détaillé, factuel, orienté preuves',
    decisionMaking: 'Méthodique, basé sur les données',
    riskTolerance: 'Modérée - calculée',
    leadershipStyle: 'Structured, détaillé, orienté processus',
    values: ['Qualité', 'Fiabilité', 'Processus', 'Conformité'],
    motivators: ['Sécurité', 'Prédictibilité', 'Excellence technique'],
    challenges: ['Rapidité de décision', 'Innovation disruptive'],
    recommendations: ['Données détaillées', 'Processus clairs', 'ROI quantifié']
  },
  {
    id: 'collaboratif',
    name: 'Collaboratif Relationnel',
    description: 'Leader centré sur l\'équipe et les relations',
    color: '#27AE60',
    characteristics: ['Collaboration', 'Relations', 'Équipe', 'Harmonie'],
    communicationStyle: 'Bienveillant, inclusif, orienté consensus',
    decisionMaking: 'Collaboratif, basé sur l\'équipe',
    riskTolerance: 'Modérée - partagée',
    leadershipStyle: 'Participatif, bienveillant, orienté équipe',
    values: ['Équipe', 'Harmonie', 'Développement', 'Relations'],
    motivators: ['Reconnaissance équipe', 'Impact humain', 'Développement'],
    challenges: ['Décisions difficiles', 'Conflits'],
    recommendations: ['Approche collaborative', 'Formation équipe', 'Communication bienveillante']
  },
  {
    id: 'resultats',
    name: 'Orienté Résultats',
    description: 'Exécutant efficace et performant',
    color: '#E74C3C',
    characteristics: ['Performance', 'Efficacité', 'Résultats', 'Action'],
    communicationStyle: 'Concis, orienté action, direct',
    decisionMaking: 'Rapide, basé sur les résultats',
    riskTolerance: 'Modérée - contrôlée',
    leadershipStyle: 'Directif, orienté performance, déléguant',
    values: ['Performance', 'Efficacité', 'Résultats', 'Excellence'],
    motivators: ['Performance', 'Reconnaissance', 'Défis', 'Autonomie'],
    challenges: ['Patience', 'Processus longs'],
    recommendations: ['Focus résultats', 'KPIs clairs', 'Communication directe']
  }
];

const mockClientData: ClientData = {
  id: 'client-exemple',
  name: 'Manufacturing Excellence Inc.',
  industry: 'Manufacturing',
  size: '500-1000 employés',
  relationship: '3 ans',
  satisfaction: 92,
  psychometricProfile: psychometricProfiles[0], // Visionnaire Stratégique
  financial: {
    ca: 15.2,
    profit: 2.8,
    budget_ti: 1.2,
    roi: 18.5,
    growth: 12.3
  },
  projects: [
    { name: 'Migration Cloud', status: 'En cours', progress: 75, budget: 250, timeline: '6 mois', priority: 'High' },
    { name: 'Sécurité NIST', status: 'Complété', progress: 100, budget: 180, timeline: '4 mois', priority: 'High' },
    { name: 'IA Analytics', status: 'Planifié', progress: 0, budget: 320, timeline: '8 mois', priority: 'Medium' },
    { name: 'Formation Équipes', status: 'En cours', progress: 45, budget: 80, timeline: '3 mois', priority: 'Medium' }
  ],
  roadmap: {
    q1: 'Migration Infrastructure',
    q2: 'Formation Équipes',
    q3: 'Optimisation Processus',
    q4: 'Innovation IA'
  },
  committee: {
    next: '2024-02-15',
    frequency: 'Mensuel',
    participants: ['CEO', 'CTO', 'CFO', 'Prime BZ']
  },
  kpis: {
    availability: 99.9,
    responseTime: 1.2,
    satisfaction: 92,
    innovation: 85
  },
  security: {
    nistScore: 87,
    compliance: 95,
    incidents: 0,
    lastAudit: '2024-01-15'
  }
};

const createSlides = (clientData: ClientData): Slide[] => [
  {
    id: 'overview',
    title: 'Vue d\'Ensemble Client',
    subtitle: 'Profil complet et métriques clés',
    icon: User,
    color: '#0066FF',
    content: (
      <div className="space-y-6">
        {/* Header Client */}
        <div className="bg-gradient-to-r from-bz-blue to-blue-700 text-white p-6 rounded-xl">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold">{clientData.name}</h3>
              <p className="text-white/90">{clientData.industry} • {clientData.size}</p>
              <p className="text-white/80">Relation: {clientData.relationship}</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold">{clientData.satisfaction}%</div>
              <div className="text-white/90">Satisfaction</div>
            </div>
          </div>
        </div>

        {/* Métriques Financières */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              <span className="text-sm text-gray-600">CA</span>
            </div>
            <div className="text-2xl font-bold text-green-600">{clientData.financial.ca}M$</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              <span className="text-sm text-gray-600">Profit</span>
            </div>
            <div className="text-2xl font-bold text-blue-600">{clientData.financial.profit}M$</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-5 h-5 text-purple-600" />
              <span className="text-sm text-gray-600">Budget TI</span>
            </div>
            <div className="text-2xl font-bold text-purple-600">{clientData.financial.budget_ti}M$</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="w-5 h-5 text-orange-600" />
              <span className="text-sm text-gray-600">ROI</span>
            </div>
            <div className="text-2xl font-bold text-orange-600">{clientData.financial.roi}%</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-5 h-5 text-red-600" />
              <span className="text-sm text-gray-600">Croissance</span>
            </div>
            <div className="text-2xl font-bold text-red-600">{clientData.financial.growth}%</div>
          </div>
        </div>

        {/* KPIs Performance */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-sm text-gray-600">Disponibilité</span>
            </div>
            <div className="text-2xl font-bold text-green-600">{clientData.kpis.availability}%</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-blue-600" />
              <span className="text-sm text-gray-600">Temps Réponse</span>
            </div>
            <div className="text-2xl font-bold text-blue-600">{clientData.kpis.responseTime}h</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-2 mb-2">
              <Star className="w-5 h-5 text-yellow-600" />
              <span className="text-sm text-gray-600">Satisfaction</span>
            </div>
            <div className="text-2xl font-bold text-yellow-600">{clientData.kpis.satisfaction}%</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-5 h-5 text-purple-600" />
              <span className="text-sm text-gray-600">Innovation</span>
            </div>
            <div className="text-2xl font-bold text-purple-600">{clientData.kpis.innovation}%</div>
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'psychometric-profile',
    title: 'Profil Psychométrique',
    subtitle: 'Analyse comportementale et recommandations',
    icon: Brain,
    color: '#8E44AD',
    content: (
      <div className="space-y-6">
        {/* Profil Psychométrique */}
        <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-6 rounded-xl border-l-4 border-purple-500">
          <div className="flex items-center gap-4 mb-4">
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold"
              style={{ backgroundColor: clientData.psychometricProfile.color }}
            >
              {clientData.psychometricProfile.name.charAt(0)}
            </div>
            <div>
              <h3 className="text-2xl font-bold text-purple-800">{clientData.psychometricProfile.name}</h3>
              <p className="text-purple-600">{clientData.psychometricProfile.description}</p>
            </div>
          </div>
        </div>

        {/* Caractéristiques */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Style de Communication
            </h4>
            <p className="text-gray-700 mb-4">{clientData.psychometricProfile.communicationStyle}</p>
            
            <h5 className="font-semibold text-gray-800 mb-2">Prise de Décision</h5>
            <p className="text-gray-600 text-sm mb-4">{clientData.psychometricProfile.decisionMaking}</p>
            
            <h5 className="font-semibold text-gray-800 mb-2">Tolérance au Risque</h5>
            <p className="text-gray-600 text-sm">{clientData.psychometricProfile.riskTolerance}</p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-green-600" />
              Style de Leadership
            </h4>
            <p className="text-gray-700 mb-4">{clientData.psychometricProfile.leadershipStyle}</p>
            
            <h5 className="font-semibold text-gray-800 mb-2">Valeurs</h5>
            <div className="flex flex-wrap gap-2">
              {clientData.psychometricProfile.values.map((value, index) => (
                <span key={index} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                  {value}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Motivateurs et Défis */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-600" />
              Motivateurs
            </h4>
            <ul className="space-y-2">
              {clientData.psychometricProfile.motivators.map((motivator, index) => (
                <li key={index} className="flex items-center gap-2 text-gray-700">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  {motivator}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              Défis Potentiels
            </h4>
            <ul className="space-y-2">
              {clientData.psychometricProfile.challenges.map((challenge, index) => (
                <li key={index} className="flex items-center gap-2 text-gray-700">
                  <Info className="w-4 h-4 text-orange-500" />
                  {challenge}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Recommandations */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border-l-4 border-blue-500">
          <h4 className="text-lg font-semibold text-blue-800 mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-blue-600" />
            Recommandations d'Approche
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {clientData.psychometricProfile.recommendations.map((recommendation, index) => (
              <div key={index} className="bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="font-medium text-gray-800">Approche {index + 1}</span>
                </div>
                <p className="text-gray-600 text-sm">{recommendation}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'projects',
    title: 'Projets et Roadmap',
    subtitle: 'Portfolio de projets et planification stratégique',
    icon: Target,
    color: '#27AE60',
    content: (
      <div className="space-y-6">
        {/* Projets en Cours */}
        <div>
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Target className="w-6 h-6 text-green-600" />
            Projets Actifs
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {clientData.projects.map((project, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm border">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h4 className="font-semibold text-gray-800">{project.name}</h4>
                    <p className="text-sm text-gray-600">{project.timeline} • {project.budget}K$</p>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    project.priority === 'High' ? 'bg-red-100 text-red-800' :
                    project.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {project.priority}
                  </span>
                </div>
                
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span>Progression</span>
                    <span>{project.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-500 h-2 rounded-full"
                      style={{ width: `${project.progress}%` }}
                    />
                  </div>
                </div>
                
                <div className="flex items-center gap-2 text-sm">
                  <div className={`w-2 h-2 rounded-full ${
                    project.status === 'Complété' ? 'bg-green-500' :
                    project.status === 'En cours' ? 'bg-blue-500' :
                    'bg-gray-400'
                  }`} />
                  <span className="text-gray-600">{project.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Roadmap 2024 */}
        <div>
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Calendar className="w-6 h-6 text-blue-600" />
            Roadmap 2024
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {Object.entries(clientData.roadmap).map(([quarter, content], index) => (
              <div key={quarter} className="bg-white p-4 rounded-lg shadow-sm border">
                <div className="text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-blue-600 font-bold">Q{index + 1}</span>
                  </div>
                  <h4 className="font-semibold text-gray-800 mb-2">{quarter.toUpperCase()}</h4>
                  <p className="text-sm text-gray-600">{content}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Comité TI */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-6 rounded-xl">
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Users className="w-6 h-6 text-gray-600" />
            Comité TI
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">Prochaine Réunion</h4>
              <p className="text-gray-600">{clientData.committee.next}</p>
              <p className="text-sm text-gray-500">Fréquence: {clientData.committee.frequency}</p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">Participants</h4>
              <div className="flex flex-wrap gap-2">
                {clientData.committee.participants.map((participant, index) => (
                  <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                    {participant}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'security',
    title: 'Sécurité et Conformité',
    subtitle: 'Posture de sécurité et conformité réglementaire',
    icon: Shield,
    color: '#E74C3C',
    content: (
      <div className="space-y-6">
        {/* Métriques Sécurité */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-5 h-5 text-red-600" />
              <span className="text-sm text-gray-600">Score NIST</span>
            </div>
            <div className="text-2xl font-bold text-red-600">{clientData.security.nistScore}%</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-sm text-gray-600">Conformité</span>
            </div>
            <div className="text-2xl font-bold text-green-600">{clientData.security.compliance}%</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              <span className="text-sm text-gray-600">Incidents</span>
            </div>
            <div className="text-2xl font-bold text-orange-600">{clientData.security.incidents}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              <span className="text-sm text-gray-600">Dernier Audit</span>
            </div>
            <div className="text-sm font-bold text-blue-600">{clientData.security.lastAudit}</div>
          </div>
        </div>

        {/* Spider Chart NIST */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h4 className="text-lg font-semibold text-gray-800 mb-4 text-center">
            🕸️ Maturité NIST Cybersecurity
          </h4>
          <div className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={[
                { function: 'IDENTIFY', asis: 85, target: 95 },
                { function: 'PROTECT', asis: 90, target: 98 },
                { function: 'DETECT', asis: 80, target: 90 },
                { function: 'RESPOND', asis: 75, target: 85 },
                { function: 'RECOVER', asis: 70, target: 80 },
                { function: 'GOVERN', asis: 88, target: 95 }
              ]}>
                <PolarGrid />
                <PolarAngleAxis dataKey="function" />
                <PolarRadiusAxis angle={90} domain={[0, 100]} />
                <Radar
                  name="ASIS"
                  dataKey="asis"
                  stroke="#E74C3C"
                  fill="#E74C3C"
                  fillOpacity={0.3}
                  strokeWidth={2}
                />
                <Radar
                  name="Target"
                  dataKey="target"
                  stroke="#27AE60"
                  fill="#27AE60"
                  fillOpacity={0.2}
                  strokeWidth={2}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-6 mt-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-red-500 rounded"></div>
              <span>ASIS</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded"></div>
              <span>Target</span>
            </div>
          </div>
        </div>

        {/* Recommandations Sécurité */}
        <div className="bg-gradient-to-r from-red-50 to-orange-50 p-6 rounded-xl border-l-4 border-red-500">
          <h4 className="text-lg font-semibold text-red-800 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-red-600" />
            Recommandations Sécurité
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h5 className="font-semibold text-gray-800 mb-2">Actions Prioritaires</h5>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Renforcer la détection d'intrusions</li>
                <li>• Améliorer les procédures de réponse</li>
                <li>• Formation équipes cybersécurité</li>
              </ul>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h5 className="font-semibold text-gray-800 mb-2">Prochaines Étapes</h5>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Audit de conformité Q2</li>
                <li>• Test de pénétration Q3</li>
                <li>• Certification ISO 27001</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    )
  }
];

export default function DashboardClientComplet({ isOpen, onClose }: DashboardClientCompletProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const slides = createSlides(mockClientData);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const currentSlideData = slides[currentSlide];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-40"
          />
          
          {/* Main Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden">
              {/* Header */}
              <div 
                className="p-6 text-white"
                style={{ backgroundColor: currentSlideData.color }}
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    <currentSlideData.icon className="w-8 h-8" />
                    <div>
                      <h2 className="text-2xl font-bold">{currentSlideData.title}</h2>
                      {currentSlideData.subtitle && (
                        <p className="text-white/90">{currentSlideData.subtitle}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-white hover:bg-white/10 rounded-full p-2 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                {/* Progress Bar */}
                <div className="mt-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span>Slide {currentSlide + 1} sur {slides.length}</span>
                    <span>{Math.round(((currentSlide + 1) / slides.length) * 100)}%</span>
                  </div>
                  <div className="w-full bg-white/20 rounded-full h-2">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
                      transition={{ duration: 0.3 }}
                      className="bg-white h-2 rounded-full"
                    />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-8 max-h-[calc(90vh-200px)] overflow-y-auto">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentSlide}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.3 }}
                  >
                    {currentSlideData.content}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Navigation */}
              <div className="flex justify-between items-center p-6 border-t bg-gray-50">
                <button
                  onClick={prevSlide}
                  disabled={currentSlide === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Précédent
                </button>
                
                <div className="flex gap-2">
                  {slides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-3 h-3 rounded-full transition-colors ${
                        index === currentSlide ? 'bg-bz-blue' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
                
                <button
                  onClick={nextSlide}
                  disabled={currentSlide === slides.length - 1}
                  className="flex items-center gap-2 px-4 py-2 bg-bz-blue hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  Suivant
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
